import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { carichi } from '../../api/acquisti';
import type { DettaglioCarico } from '../../api/acquisti';
import { formattaData, formattaNumero } from '../../lib/formato';
import { ChiediMotivo } from './ChiediMotivo';
import { usePuo } from '../sessione/ContestoSessione';

interface Props {
  caricoId: string;
  onChiudi: () => void;
  onCambiato: () => void;
}

export function SchedaCarico({ caricoId, onChiudi, onCambiato }: Props) {
  const puoFare = usePuo();
  const [carico, setCarico] = useState<DettaglioCarico | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [chiedeMotivo, setChiedeMotivo] = useState(false);

  useEffect(() => {
    let annullato = false;
    setCarico(null);
    setErrore(null);
    setChiedeMotivo(false);
    carichi
      .leggi(caricoId)
      .then((c) => !annullato && setCarico(c))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [caricoId]);

  async function annulla(motivo: string) {
    setChiedeMotivo(false);
    setInCorso(true);
    setErrore(null);
    try {
      setCarico(await carichi.annulla(caricoId, motivo));
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (errore && !carico) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!carico) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico la bolla…</p>
      </aside>
    );
  }

  return (
    <aside className="scheda" aria-label={`Bolla ${carico.numeroDdt}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{carico.numero ?? 'senza numero'}</strong>
            <span className={`stato stato-${carico.stato.toLowerCase()}`}>{carico.stato}</span>
            {carico.haNonConformita && <span className="stato in-ritardo">con respinte</span>}
          </p>
          <h2>
            Bolla {carico.numeroDdt} — {carico.fornitore ?? carico.codiceFornitoreMago}
          </h2>
          <p className="riferimenti">
            del {formattaData(carico.dataDdt)} · ricevuta il {formattaData(carico.dataRicevimento)}
            {carico.ricevutoDa && ` da ${carico.ricevutoDa}`}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {carico.motivoAnnullamento && (
        <p className="avviso">
          Annullata: {carico.motivoAnnullamento}. Le quantità sono tornate da ricevere sugli
          ordini.
        </p>
      )}

      {carico.note && <p className="note">{carico.note}</p>}

      <table className="righe-richiesta">
        <thead>
          <tr>
            <th>Cosa</th>
            <th className="destra">Accettata</th>
            <th className="destra">Respinta</th>
          </tr>
        </thead>
        <tbody>
          {carico.righe.map((riga) => (
            <tr key={riga.id}>
              <td>
                <span className="descrizione">{riga.descrizione}</span>
                <span className="dettaglio">
                  {riga.numeroOrdine && <span>ordine {riga.numeroOrdine}</span>}
                  {riga.motivoRifiuto && <em className="motivo">respinta: {riga.motivoRifiuto}</em>}
                  {riga.note && <span>{riga.note}</span>}
                </span>
              </td>
              <td className="destra">
                {formattaNumero(riga.quantitaAccettata)} {riga.unitaMisura}
              </td>
              <td className="destra">
                {riga.quantitaRifiutata > 0 ? formattaNumero(riga.quantitaRifiutata) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {errore && <p className="errore">{errore}</p>}

      {chiedeMotivo ? (
        <ChiediMotivo
          domanda="Perché si annulla questa bolla?"
          etichettaConferma="Annulla la bolla"
          inCorso={inCorso}
          onConferma={(motivo) => void annulla(motivo)}
          onAnnulla={() => setChiedeMotivo(false)}
        />
      ) : (
        carico.stato === 'Registrato' && puoFare('AnnullaCarichi') && (
          <div className="azioni">
            <button
              type="button"
              className="pericolo"
              disabled={inCorso}
              title="Le quantità tornano da ricevere sugli ordini"
              onClick={() => setChiedeMotivo(true)}
            >
              Annulla la bolla
            </button>
          </div>
        )
      )}
    </aside>
  );
}
